import { useCallback, useEffect, useMemo, useState } from "react";
import { ROLE_OPTIONS } from "../../../constants/roles";
import {
  isValidEmail,
  isValidPassword,
  passwordStrength,
} from "../../../utils/validators";

const DEFAULT_FORM = Object.freeze({
  username: "",
  password: "",
  confirmPassword: "",
});

const normalizeRoles = (values) => {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const normalized = [];
  values.forEach((value) => {
    const upper = String(value || "").toUpperCase();
    if (!upper || seen.has(upper)) return;
    seen.add(upper);
    normalized.push(upper);
  });
  return normalized;
};

export default function useLoginCredentialsForm(options = {}) {
  const { defaultRoles, includeRole = false, includeEmail = true } = options;

  const initialRoles = useMemo(() => {
    const normalized = normalizeRoles(defaultRoles);
    return normalized.length > 0 ? normalized : normalizeRoles(["AGENT"]);
  }, [defaultRoles]);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [roles, setRoles] = useState(initialRoles);
  const [touched, setTouched] = useState({
    username: false,
    password: false,
    confirmPassword: false,
  });
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    setRoles(initialRoles);
  }, [initialRoles]);

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const markTouched = useCallback((name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  }, []);

  const toggleRole = useCallback((value) => {
    setRoles((prev) => {
      const normalized = String(value || "").toUpperCase();
      return prev.includes(normalized)
        ? prev.filter((role) => role !== normalized)
        : [...prev, normalized];
    });
  }, []);

  const selectAll = useCallback(() => {
    setRoles(ROLE_OPTIONS.map((opt) => opt.value));
  }, []);

  const clearAll = useCallback(() => {
    setRoles([]);
  }, []);

  const emailValue = formData.username;
  const trimmedEmail = typeof emailValue === "string" ? emailValue.trim() : "";
  const emailInvalid = includeEmail
    ? !isValidEmail(trimmedEmail)
    : trimmedEmail
      ? !isValidEmail(trimmedEmail)
      : false;

  const passwordInvalid = !isValidPassword(formData.password);
  const confirmInvalid =
    !formData.confirmPassword ||
    formData.password !== formData.confirmPassword;
  const rolesInvalid = includeRole && roles.length === 0;

  const errors = {
    email: emailInvalid,
    password: passwordInvalid,
    confirm: confirmInvalid,
    roles: rolesInvalid,
  };

  const shouldShow = {
    email: includeEmail && (showErrors || touched.username),
    password: showErrors || touched.password,
    confirm: showErrors || touched.confirmPassword,
  };

  const strength = passwordStrength(formData.password);

  const canSubmit =
    (!includeEmail || !errors.email) &&
    !errors.password &&
    !errors.confirm &&
    (!includeRole || !errors.roles);

  const buildPayload = useCallback(
    () => ({
      username: trimmedEmail,
      password: formData.password,
      roles,
    }),
    [formData.password, roles, trimmedEmail],
  );

  return {
    formData,
    setField,
    markTouched,
    roles,
    toggleRole,
    selectAll,
    clearAll,
    errors,
    shouldShow,
    strength,
    canSubmit,
    buildPayload,
    setShowErrors,
  };
}
